import { useState } from "react"

import { useAuth } from "@/core/auth/client/AuthProvider"
import { notifyRegisterError } from "@/features/v2/auth/register/actions/notifyRegisterError"
import { registerWithGoogle } from "@/features/v2/auth/register/actions/registerWithGoogle"
import type { RegisterError } from "@/features/v2/auth/register/actions/result"

// pending covers prep + OAuth kickoff; on success the page usually redirects immediately,
// so a separate success state is unnecessary.
export type GoogleRegisterState = "idle" | "pending" | "error"

export type UseGoogleRegisterParams = {
  welcomeBonus: string
  affiliateCode: string
  partner: string | null
  selectedCountryCode: string
  getCountry?: () => Promise<string>
  /** Optional parent UI signal (e.g. full-page spinner). Button disable uses `state`. */
  onPendingChange?: (pending: boolean) => void
}

export const useGoogleRegister = ({
  welcomeBonus,
  affiliateCode,
  partner,
  selectedCountryCode,
  getCountry,
  onPendingChange
}: UseGoogleRegisterParams) => {
  const { signInWithGoogle } = useAuth()
  const [state, setState] = useState<GoogleRegisterState>("idle")
  const [error, setError] = useState<RegisterError | null>(null)

  const handleGoogleRegister = async (): Promise<void> => {
    setState("pending")
    setError(null)
    onPendingChange?.(true)

    const country = getCountry ? await getCountry() : selectedCountryCode

    const result = await registerWithGoogle({
      welcomeBonus,
      affiliateCode,
      partner,
      selectedCountryCode: country,
      signInWithGoogle
    })

    if (!result.ok) {
      setError(result.error)
      setState("error")
      onPendingChange?.(false)
      notifyRegisterError(result.error)
      return
    }

    // Success path typically redirects to Google immediately; leave pending until unload.
  }

  return { handleGoogleRegister, state, error }
}
